import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { LayoutDashboard } from 'lucide-react';
import { useRef } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { dashboardTabPath, useAllowedDashboardTabs } from './dashboardTabs';
import SaleDashboard from './SaleDashboard';
import OrderDashboard from './OrderDashboard';
import VehicleDashboard from './VehicleDashboard';
import QuoteDashboard from './QuoteDashboard';
import CustomerDashboard from './CustomerDashboard';
import ProductDashboard from './ProductDashboard';

/**
 * Hub /dashboards/:tab — barra de chips (tablist) que alterna entre os 6
 * dashboards existentes sem sair da tela (prototipo-dashboards.html).
 * A aba vem da rota (links diretos, refresh e voltar do navegador funcionam);
 * aba ausente, desconhecida ou sem permissão redireciona para a primeira
 * permitida (preferindo Vendas). Cada painel é o componente original, intacto.
 * No shell mobile não há barra: cada aba é uma tela (o "Mais" lista as abas),
 * como era com as rotas /dashboard/* antigas.
 */
const PANELS = {
    sale: SaleDashboard,
    order: OrderDashboard,
    vehicle: VehicleDashboard,
    quote: QuoteDashboard,
    customer: CustomerDashboard,
    product: ProductDashboard,
};

const chipSx = (selected) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: 34,
    px: '14px',
    borderRadius: '10px',
    border: '1px solid',
    borderColor: selected ? 'primary.main' : 'divider',
    backgroundColor: selected ? 'info.bg' : 'background.paper',
    color: selected ? 'primary.main' : 'text.secondary',
    font: 'inherit',
    fontSize: 12.5,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'border-color .15s, color .15s, background .15s',
    '&:hover': { borderColor: 'primary.main', color: 'primary.main', ...(selected ? {} : { backgroundColor: 'action.hover' }) },
    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '2px' },
    '& svg': { width: 15, height: 15, flexShrink: 0 },
});

export default function DashboardsHub() {
    const { tab } = useParams();
    const navigate = useNavigate();
    const isMobileShell = useIsMobileShell();
    const allowed = useAllowedDashboardTabs();
    const tabRefs = useRef({});

    if (allowed.length === 0) {
        return (
            <PageScaffold links={[]} titlePageTarget="Dashboards">
                <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }} role="alert">
                    <Box sx={{ width: 48, height: 48, mx: 'auto', mb: 1.5, borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'info.bg', color: 'primary.main' }}>
                        <LayoutDashboard size={22} strokeWidth={2} />
                    </Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'text.primary' }}>Acesso restrito</Typography>
                    <Typography sx={{ fontSize: 13.5, mt: 0.5 }}>Você não tem permissão para visualizar os dashboards.</Typography>
                </Box>
            </PageScaffold>
        );
    }

    const active = allowed.find((t) => t.id === tab);
    if (!active) {
        const first = allowed.find((t) => t.id === 'sale') || allowed[0];
        return <Navigate to={dashboardTabPath(first.id)} replace />;
    }

    const Panel = PANELS[active.id];
    if (isMobileShell) return <Panel />;

    const select = (id, focus) => {
        navigate(dashboardTabPath(id));
        if (focus) requestAnimationFrame(() => tabRefs.current[id]?.focus());
    };

    const onKeyDown = (event) => {
        const index = allowed.findIndex((t) => t.id === active.id);
        let next = null;
        if (event.key === 'ArrowRight') next = allowed[(index + 1) % allowed.length];
        else if (event.key === 'ArrowLeft') next = allowed[(index - 1 + allowed.length) % allowed.length];
        else if (event.key === 'Home') next = allowed[0];
        else if (event.key === 'End') next = allowed[allowed.length - 1];
        if (!next) return;
        event.preventDefault();
        select(next.id, true);
    };

    return (
        <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
            <Box
                role="tablist"
                aria-label="Dashboards"
                onKeyDown={onKeyDown}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', maxWidth: '100%', mb: '18px' }}
            >
                {/* <Typography component="span" aria-hidden="true" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.disabled', mr: '2px' }}>
                    Dashboards
                </Typography> */}
                {allowed.map((t) => {
                    const selected = t.id === active.id;
                    const Icon = t.icon;
                    return (
                        <Box
                            key={t.id}
                            component="button"
                            type="button"
                            role="tab"
                            id={`tab-${t.id}`}
                            aria-selected={selected}
                            aria-controls={`panel-${t.id}`}
                            tabIndex={selected ? 0 : -1}
                            ref={(el) => { tabRefs.current[t.id] = el; }}
                            onClick={() => { if (!selected) select(t.id, false); }}
                            sx={chipSx(selected)}
                        >
                            <Icon aria-hidden="true" strokeWidth={2} />
                            {t.label}
                        </Box>
                    );
                })}
            </Box>
            <Box role="tabpanel" id={`panel-${active.id}`} aria-labelledby={`tab-${active.id}`} tabIndex={-1} sx={{ outline: 'none' }}>
                <Panel key={active.id} />
            </Box>
        </Box>
    );
}
